import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAPITAL_OPTIONS = ["$300K – $500K", "$500K – $1M", "$1M – $2.5M", "$2.5M+"];
const NET_WORTH_OPTIONS = ["$1M – $2.5M", "$2.5M – $5M", "$5M+"];

const inputCls =
  "w-full rounded-xl border border-[#E4DFD3] bg-white px-4 py-3 text-sm text-[#142526] placeholder:text-[#9AA5A5] focus:outline-none focus:ring-2 focus:ring-[#FF6B4A] focus:border-transparent transition";

export function LeadForm({ image }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", market: "", capital_range: "", net_worth_range: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
      toast.success("Request received — our franchise team will reach out within 1 business day.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong. Please check your details and try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="lead-form" className="py-20 md:py-28" data-testid="lead-form-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <div className="rounded-3xl overflow-hidden border border-[#E4DFD3] aspect-[4/3] hidden lg:block">
            <img src={image} alt="Dog enjoying the Dogtopia spa" className="w-full h-full object-cover" />
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="bg-white rounded-3xl border border-[#E4DFD3] p-8 md:p-10 shadow-xl" data-testid="lead-capture-form">
            {submitted ? (
              <div className="text-center py-10" data-testid="lead-form-success">
                <CheckCircle2 className="w-14 h-14 text-[#FF6B4A] mx-auto mb-5" />
                <h3 className="font-display text-2xl font-bold text-[#0D3B3E] mb-3">You're on the list!</h3>
                <p className="text-[#5C6B6C] leading-relaxed max-w-sm mx-auto">
                  Your franchise information request has been received. A member of our development team will contact you
                  within one business day with your info pack.
                </p>
              </div>
            ) : (
              <>
                <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#FF6B4A] mb-3">Request Info</p>
                <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0D3B3E] mb-6">
                  Start your franchise journey today
                </h2>
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="lead-form-name-input" required minLength={2} placeholder="Full Name" value={form.name} onChange={set("name")} className={inputCls} />
                    <input data-testid="lead-form-phone-input" required minLength={7} type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} />
                  </div>
                  <input data-testid="lead-form-email-input" required type="email" placeholder="Email Address" value={form.email} onChange={set("email")} className={inputCls} />
                  <input data-testid="lead-form-market-input" required minLength={2} placeholder="Target Market (City, State)" value={form.market} onChange={set("market")} className={inputCls} />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <select data-testid="lead-form-capital-select" required value={form.capital_range} onChange={set("capital_range")} className={inputCls}>
                      <option value="" disabled>Liquid Capital Available</option>
                      {CAPITAL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <select data-testid="lead-form-networth-select" required value={form.net_worth_range} onChange={set("net_worth_range")} className={inputCls}>
                      <option value="" disabled>Total Net Worth</option>
                      {NET_WORTH_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="lead-form-submit-button"
                    className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FF6B4A] hover:bg-[#E55636] disabled:opacity-60 text-white font-semibold px-7 py-4 transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  >
                    {submitting ? "Sending..." : "Get My Franchise Info Pack"}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </button>
                  <p className="text-[11px] text-[#9AA5A5] leading-relaxed text-center">
                    By submitting, you agree to be contacted about franchise opportunities. This is not an offer to sell a
                    franchise; offers are made only by FDD.
                  </p>
                </form>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
